/* Membership Api Module */
import axios, { type AxiosInstance } from 'axios';
import { UtilConstantS } from '../../../../../modules/Util/S/Constant/module/UtilConstantS';
import {
  AXIOS_TIME_OUT,
  CheckedRewardType,
  CreateMembershipProjectResType,
  IsRewardsIncludedReturnType,
  CreateSupportPaymentResType,
  Membership,
  MembershipAuthKeyResType,
  PartialMembership,
  SearchQueryItem,
  Support,
  QueriedMembershipCreator,
  Commission,
  GetOrCreateCietyResType,
  DeleteCietyResType,
  MembershipSummary,
} from '../type';

/** @todo 추후 환경별 도메인 확정 시 변경 필요 */
export const MEMBERSHIP_SERVICE_URL = process.env.MEMBERSHIP_SERVICE_URL || 'https://memb.dev.ciety.dev';

export type IsRewardsIncludedType = (
  membershipIds: string[],
  rewards: CheckedRewardType[],
  config?: { timeout: number },
) => Promise<IsRewardsIncludedReturnType>;

interface IMembershipClient {
  isRewardsIncluded: IsRewardsIncludedType;
}

/** @desc 멤버십 서비스와 소통하는 abstract 클래스입니다.
 * 원하는 비즈니스 로직은 해당 클래스에 구현 바랍니다. */
abstract class AbstractMembershipClient implements IMembershipClient {
  readonly membership_token: string;
  readonly membership_api_client: AxiosInstance;
  constructor(token: string) {
    this.membership_token = token;
    this.membership_api_client = axios.create({
      baseURL: MEMBERSHIP_SERVICE_URL,
      headers: {
        'x-api-key': this.membership_token,
      },
    });

    this.membership_api_client.interceptors.response.use(
      (response) => {
        // 응답 성공 시 처리
        return response;
      },
      (error) => {
        // 에러 메시지 변환 (예: 사용자에게 보여줄 메시지)
        if (error.response) {
          // 서버 응답이 있는 경우 (4xx, 5xx)
          console.error('Response data:', error.response.data);
        } else if (error.request) {
          // 요청은 전송됐으나 응답을 받지 못한 경우
          console.error('No response received:', error.request);
        } else {
          // 요청 설정 중에 발생한 에러
          console.error('Error during request setup:', error.message);
        }

        return Promise.reject(error);
      },
    );
  }

  async createMembershipProject(storeId: string) {
    const { data } = await this.membership_api_client.post<CreateMembershipProjectResType>(
      '/create-project',
      {
        service: process.env.SERVICE_APP_NAME ?? UtilConstantS.SERVICE_APP_NAME.MARPPLE_SHOP,
        storeId,
      },
    );

    return data;
  }

  async createSupportPayment(body: {
    payMethodId: string;
    supportId: string;
    price: number;
    currency: string;
    message: string;
    imageUrls: string[];
  }) {
    const { data } = await this.membership_api_client.post<CreateSupportPaymentResType>(
      '/create-support-payment',
      body,
    );

    return data;
  }

  /**
   * membership 조회 (토큰 불필요)
   */
  static async getMembership(membershipId: string): Promise<Membership> {
    const {
      data: { data },
    } = await axios.post('/membership', { membershipId }, { baseURL: MEMBERSHIP_SERVICE_URL });

    return data;
  }

  async getMemberships(projectId: string): Promise<Membership[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/memberships', { projectId });

    return items;
  }

  /**
   * projectId로 membership 목록 조회 (토큰 불필요)
   */
  static async getMemberships(projectId: string): Promise<Membership[]> {
    const {
      data: {
        data: { items },
      },
    } = await axios.post('/memberships', { projectId }, { baseURL: MEMBERSHIP_SERVICE_URL });

    return items;
  }

  /**
   * membership 목록 조회 (토큰 불필요)
   */
  static async getQueryMemberships(
    data: { projectIds: string[] },
    type: 'USER',
  ): Promise<PartialMembership[]>;

  static async getQueryMemberships(
    data: { membershipIds: string[] },
    type: 'USER',
  ): Promise<PartialMembership[]>;

  static async getQueryMemberships(
    data: { membershipIds?: string[]; projectIds?: string[] },
    type: 'USER',
  ): Promise<PartialMembership[]> {
    const {
      data: {
        data: { items },
      },
    } = await axios.post(
      '/query-memberships',
      { membershipIds: data.membershipIds, projectIds: data.projectIds, type },
      { baseURL: MEMBERSHIP_SERVICE_URL },
    );

    return items;
  }

  /**
   * membership 목록 조회 (토큰 필요)
   */
  async getQueryMemberships(
    data: { projectIds: string[] },
    type: 'CREATOR',
  ): Promise<QueriedMembershipCreator[]>;

  async getQueryMemberships(
    data: { membershipIds: string[] },
    type: 'CREATOR',
  ): Promise<QueriedMembershipCreator[]>;

  async getQueryMemberships(
    data: { membershipIds?: string[]; projectIds?: string[] },
    type: 'CREATOR',
  ): Promise<QueriedMembershipCreator[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/query-memberships', {
      membershipIds: data.membershipIds,
      projectIds: data.projectIds,
      type,
    });

    return items;
  }

  async getMypageSubscriptions(): Promise<Membership[]> {
    const {
      data: {
        data: { memberships, supports },
      },
    } = await this.membership_api_client.post('/mypage-subscriptions');

    return [...memberships, ...supports];
  }

  async getMyMembershipByProject(projectId: string): Promise<Membership[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/subscribed-memberships', { projectId });

    return items;
  }

  async getMembershipSummaryByProject(projectId: string): Promise<MembershipSummary[]> {
    try {
      const {
        data: {
          data: { items },
        },
      } = await this.membership_api_client.post('/memberships-summary', { projectId });

      return items;
    } catch (e) {
      /* 멤버십 서버 다운에도 정상동작을 위해 */
      console.error(e);
      return [];
    }
  }

  async isRewardsIncluded(
    membershipIds: string[],
    rewards: CheckedRewardType[],
    config?: { timeout: number },
  ): Promise<IsRewardsIncludedReturnType> {
    try {
      const requestConfig = config?.timeout ? { timeout: config.timeout } : {};

      const {
        data: { code, data },
      } = await this.membership_api_client.post(
        '/is-rewards-included',
        {
          payload: {
            type: 'membership',
            membershipIds,
            rewards,
          },
        },
        requestConfig,
      );

      return code === '000'
        ? {
            success: true,
            data: data.memberships,
          }
        : {
            success: false,
          };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          // 타임아웃 발생
          return {
            success: false,
            error_code: AXIOS_TIME_OUT,
          };
        }
      }
      // 다른 에러의 경우도 false 반환
      return {
        success: false,
      };
    }
  }

  /**
   * project 내 membership 이름 기반 검색
   */
  async getQuerySearchMemberships(projectId: string, query: string): Promise<SearchQueryItem[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/search-memberships', {
      projectId,
      query,
    });

    return items;
  }

  /**
   * project 내 support 이름 기반 검색
   */
  async getQuerySearchSupports(projectId: string, query: string): Promise<SearchQueryItem[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/search-supports', {
      projectId,
      query,
    });

    return items;
  }

  /**
   * project 내 commission 이름 기반 검색
   */
  async getQuerySearchCommissions(projectId: string, query: string): Promise<SearchQueryItem[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/search-commissions', {
      projectId,
      query,
    });

    return items;
  }

  async getMembershipSettings(projectId: string): Promise<Membership[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/membership-settings', {
      projectId,
    });

    return items;
  }

  /**
   * support 조회 (토큰 불필요)
   */
  static async getSupport(supportId: string): Promise<Support> {
    const {
      data: { data },
    } = await axios.post('/support', { supportId }, { baseURL: MEMBERSHIP_SERVICE_URL });

    return data;
  }

  async getSupportPayment(supportId: string): Promise<Support> {
    const {
      data: { data },
    } = await this.membership_api_client.post('/support-payment', { supportId });

    return data;
  }

  async getSupports(projectId: string): Promise<Support[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/supports', { projectId });

    return items;
  }

  /**
   * @desc 내가 팔고 있는 멤버십 중 구독자가 있는 멤버십 수 조회
   * 회원 탈퇴 시 표시를 위함.
   */
  async getMySubscribedMembershipsForWithdrawal(): Promise<number> {
    const {
      data: {
        data: { membershipCount },
      },
    } = await this.membership_api_client.post('/check-withdrawal-user');

    return membershipCount;
  }

  /**
   * membership 존재 여부 조회 (토큰 불필요)
   */
  static async existMembership(project_id: string): Promise<{
    ok: boolean;
    membership: boolean;
    membershipOpen: boolean;
    support: boolean;
    commission: boolean;
    dao: GetOrCreateCietyResType['data'];
  }> {
    const initial_data = {
      data: {
        data: {
          // 멤버십(오픈 or 삭제예정) or 후원 (오픈 or 클로즈) or 커미션 (오픈 or 클로즈)
          ok: false,
          // 멤버십(오픈 or 삭제예정)
          membership: false,
          // 멤버십(오픈)
          membershipOpen: false,
          // 후원(오픈 or 클로즈)
          support: false,
          // 커미션(오픈 or 클로즈)
          commission: false,
          // 사이어티(연동 or 미연동)
          dao: null,
        },
      },
    };

    const {
      data: {
        data: {
          // 멤버십(오픈 or 삭제예정) or 후원 (오픈 or 클로즈) or 커미션 (오픈 or 클로즈)
          ok,
          // 멤버십(오픈 or 삭제예정)
          membership,
          // 멤버십(오픈)
          membershipOpen,
          // 후원(오픈 or 클로즈)
          support,
          // 커미션(오픈 or 클로즈)
          commission,
          // 사이어티(연동 or 미연동)
          dao = null,
        },
      },
    } = await axios
      .post('/main-banner-for-shop', { projectId: project_id }, { baseURL: MEMBERSHIP_SERVICE_URL })
      .catch(() => initial_data);

    return { ok, membership, membershipOpen, support, commission, dao };
  }

  /**
   * supports 목록 조회 (토큰 필요)
   */
  async getQuerySupports(supportIds: string[], type: 'CREATOR'): Promise<PartialMembership[]> {
    const {
      data: {
        data: { items },
      },
    } = await this.membership_api_client.post('/query-supports', {
      supportIds,
      type,
    });

    return items;
  }

  /**
   * commission 목록 조회
   */
  async getQueryCommissions(commissionRequestIds: string[]): Promise<Commission[]> {
    const {
      data: { data },
    } = await axios.post('/query-commissions', { commissionRequestIds }, { baseURL: MEMBERSHIP_SERVICE_URL });

    return data?.items;
  }

  /**
   * commission 목록 조회
   */
  async getCommission(commissionRequestId: string): Promise<Commission[]> {
    const {
      data: { data },
    } = await axios.post('/mypage-commission', { commissionRequestId }, { baseURL: MEMBERSHIP_SERVICE_URL });

    return data;
  }

  /**
   * @description 사이어티 조회
   * 샵과 연동된 사이어티를 조회합니다.
   */
  async getCiety(projectId: string) {
    const { data } = await this.membership_api_client.post<GetOrCreateCietyResType>('/project-dao', {
      projectId,
    });

    return data;
  }

  /**
   * @description 사이어티 연동
   * 마플샵에 사이어티를 연동하기 위해서는 MCO회원에 “샵”이 존재해야 합니다.
   * 즉, projectId + subdomain 마플샵, 커뮤니티 둘다 같은 계정이어야 연동됩니다.
   */
  async connect(projectId: string, subdomain: string) {
    const { data } = await this.membership_api_client.post<GetOrCreateCietyResType>('/connect-project-dao', {
      projectId,
      subdomain,
    });

    return data;
  }

  /**
   * @description 사이어티 연동 해제
   * 사이어티와 마플샵 스토어 연동을 해제합니다.
   * 연동을 해제하면, 모든 멤버십 플랜의 멤버십 그룹이 삭제됩니다
   * 멤버십 그룹 관련 커뮤니티 설정이 모두 리셋됩니다.
   * by Ciety 사이드
   */
  async disconnect(projectId: string, daoId: string) {
    const { data } = await this.membership_api_client.post<DeleteCietyResType>('/disconnect-project-dao', {
      projectId,
      daoId,
    });

    return data;
  }
}

export type MembershipClientInstanceType = AbstractMembershipClient;

/** @desc 멤버십 서비스 사용자가 본인인 경우 사용하는 클래스입니다.
 *
 * @example ```
 * const membership_client = await MembershipApiS.OwnerMembershipClient.create(authorization_code);
 * const something = await membership_client.getSomething()
 * ```
 * */
export class OwnerMembershipClient extends AbstractMembershipClient {
  static async getToken(authorization_code: string) {
    const res = await axios.post<MembershipAuthKeyResType>(`${MEMBERSHIP_SERVICE_URL}/auth-key`, {
      provider: 'MCO',
      token: authorization_code,
    });
    return res.data.data.token;
  }

  /** @desc authorization_code 는 로그인 시 req.session.authorization_code 에 저장됩니다. 해당 값을 활용하시기 바랍니다. */
  static async create(authorization_code: string) {
    const token = await OwnerMembershipClient.getToken(authorization_code);
    return new OwnerMembershipClient(token);
  }
}

/** @desc 멤버십 서비스 사용자가 본인이 아닌 대리자인 경우 사용하는 클래스입니다.
 *
 * @example ```
 * const membership_client = await MembershipApiS.ProxyMembershipClient.create(auth_uid);
 * const something = await membership_client.getSomething()
 * ```
 * */
export class ProxyMembershipClient extends AbstractMembershipClient {
  static async getToken(data: {
    owner_uid: string;
    proxy_uid: string;
    store_id?: string;
    project_id?: string;
  }) {
    const res = await axios.post<MembershipAuthKeyResType>(`${MEMBERSHIP_SERVICE_URL}/auth-key-for-manager`, {
      provider: 'MCO',
      uid: data.owner_uid,
      managerUid: data.proxy_uid,
      payload: {
        storeId: data.store_id,
        projectId: data.project_id,
        type: data.project_id ? 'MARPPLE_SHOP_COMMON' : 'MARPPLE_SHOP_TEMP',
      },
    });

    return res.data.data.token;
  }

  /** @desc owner_uid, proxy_uid 는 users 테이블에 있는 Mauth와 소통을 위해 필요한 id 컬럼입니다. 해당 값을 활용하시기 바랍니다. */
  static async create(data: {
    owner_uid: string;
    proxy_uid: string;
    store_id?: string;
    project_id?: string;
  }) {
    const token = await ProxyMembershipClient.getToken(data);
    return new ProxyMembershipClient(token);
  }
}
